'use client';

import { useRouter } from 'next/navigation';
import { Play, Users, ChevronDown, ChevronUp, ChevronsUpDown, Search, Trash2 } from 'lucide-react';
import { ProjectsTableProps } from '@/shared/properties/ProjectTable';
import {
  ALL_PROJECT_STATUSES,
  ProjectStatus,
  PROJECT_STATUS_DOT,
  PROJECT_STATUS_TEXT,
  resolveProjectStatus,
} from '@/shared/lib/types';
import type { Project, ProjectSortByField } from '@/shared/lib/types';
import { useProjectsTable } from '@/features/projects/hooks/useProjectsTable';
import { useI18nLanguage, useNamespacedTranslate } from '@/shared/lib/locales';
import { Pagination } from '@/shared/components/Pagination';

const TYPE_STYLES: Record<string, string> = {
  'Technical Docs': 'bg-green-50 text-green-700 border border-green-200',
  'Research': 'bg-teal-50 text-teal-700 border border-teal-200',
  'General': 'bg-red-50 text-red-600 border border-red-200',
};

const ALL_TYPES = ['Technical Docs', 'Research', 'General'];

function deriveStatus(project: Project): ProjectStatus {
  return resolveProjectStatus(project.status, project.is_active);
}

interface SortableHeaderProps {
  label: string;
  column: ProjectSortByField;
  sortBy?: ProjectSortByField | null;
  sortOrder?: 'asc' | 'desc' | null;
  onSortChange?: (column: ProjectSortByField) => void;
  align?: 'left' | 'right';
  widthClassName?: string;
}

// A tabela usa `table-fixed` (ver ProjectsTable) para nunca poder crescer
// além do container — o que exige uma largura explícita por coluna aqui, e
// truncar (não só impedir a quebra de linha) qualquer título que ainda assim
// não caiba, em vez de deixar o texto transbordar para a coluna vizinha.
function SortableHeader({ label, column, sortBy, sortOrder, onSortChange, align = 'left', widthClassName }: SortableHeaderProps) {
  const isActive = sortBy === column;
  const Icon = isActive ? (sortOrder === 'asc' ? ChevronUp : ChevronDown) : ChevronsUpDown;

  if (!onSortChange) {
    return <th className={`text-${align} px-5 py-3 font-medium text-gray-500 truncate ${widthClassName ?? ''}`}>{label}</th>;
  }

  return (
    <th className={`text-${align} px-5 py-3 font-medium text-gray-500 ${widthClassName ?? ''}`}>
      <button
        type="button"
        onClick={() => onSortChange(column)}
        className={`flex items-center gap-1 min-w-0 max-w-full hover:text-gray-700 transition-colors ${align === 'right' ? 'ml-auto' : ''} ${isActive ? 'text-gray-700' : ''}`}
      >
        <span className="truncate">{label}</span>
        <Icon className={`h-3.5 w-3.5 shrink-0 ${isActive ? 'text-gray-700' : 'text-gray-300'}`} />
      </button>
    </th>
  );
}

export function ProjectsTable({
  projects,
  onRun,
  onManageCollaborators,
  onDelete,
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  sortBy,
  sortOrder,
  onSortChange,
}: ProjectsTableProps) {
  const t = useNamespacedTranslate('projectsTable');
  const tProjectStatus = useNamespacedTranslate('projectStatus');
  const uiLanguage = useI18nLanguage();

  const router = useRouter();
  const {
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    typeOpen,
    setTypeOpen,
    statusOpen,
    setStatusOpen,
    filtered,
    isProjectAdmin,
  } = useProjectsTable(projects, onDelete);

  return (
    <div className="w-full space-y-4">
      {/* Toolbar */}
      <div className="flex items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder={t('searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 placeholder:text-gray-400"
          />
        </div>

        {/* Status filter */}
        <div className="relative">
          <button
            onClick={() => { setStatusOpen((o) => !o); setTypeOpen(false); }}
            className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 hover:bg-gray-50"
          >
            {statusFilter ? tProjectStatus(statusFilter) : t('allStatus')}
            <ChevronDown className="h-4 w-4 text-gray-400" />
          </button>
          {statusOpen && (
            <div className="absolute z-10 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-md">
              <button
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                onClick={() => { setStatusFilter(''); setStatusOpen(false); }}
              >
                {t('allStatus')}
              </button>
              {ALL_PROJECT_STATUSES.map((s) => (
                <button
                  key={s}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  onClick={() => { setStatusFilter(s); setStatusOpen(false); }}
                >
                  {tProjectStatus(s)}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        {/* table-fixed: a largura de cada coluna vem só do que é definido
            aqui no cabeçalho, nunca do conteúdo das linhas — é o que garante
            que a tabela não consegue crescer além do container (a soma dos
            w-[…%] tem de ficar em 100%), ao contrário do layout automático
            (table-auto), em que uma célula com texto comprido empurra a
            tabela inteira para além do cartão. */}
        <table className="w-full text-sm table-fixed">
          <thead>
            <tr className="border-b border-gray-100">
              <SortableHeader label={t('nameHeader')} column="name" sortBy={sortBy} sortOrder={sortOrder} onSortChange={onSortChange} widthClassName="w-[16%]" />
              <SortableHeader label={t('createdByHeader')} column="created_by" sortBy={sortBy} sortOrder={sortOrder} onSortChange={onSortChange} widthClassName="w-[13%]" />
              <th className="text-left px-5 py-3 font-medium text-gray-500 truncate w-[15%]">{t('collaboratorsHeader')}</th>
              <SortableHeader label={t('documentsHeader')} column="files_count" sortBy={sortBy} sortOrder={sortOrder} onSortChange={onSortChange} widthClassName="w-[18%]" />
              <SortableHeader label={t('statusHeader')} column="status" sortBy={sortBy} sortOrder={sortOrder} onSortChange={onSortChange} widthClassName="w-[14%]" />
              <SortableHeader label={t('createdAtHeader')} column="created_at" sortBy={sortBy} sortOrder={sortOrder} onSortChange={onSortChange} widthClassName="w-[12%]" />
              <th className="text-right px-5 py-3 font-medium text-gray-500 truncate w-[12%]">{t('actionsHeader')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center px-5 py-8 text-gray-400 text-sm">
                  {t('emptyText')}
                </td>
              </tr>
            ) : (
              filtered.map((project, i) => {
                const status = deriveStatus(project);
                return (
                  <tr
                    key={project.project_id}
                    onClick={() => router.push(`/projects/${project.project_id}`)}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <td className="px-5 py-4 font-medium text-gray-900">
                      <button
                        onClick={(e) => { e.stopPropagation(); router.push(`/projects/${project.project_id}`); }}
                        title={project.name}
                        className="block w-full text-left line-clamp-2 break-words hover:underline hover:text-gray-700 transition-colors"
                      >
                        {project.name}
                      </button>
                    </td>
                    <td className="px-5 py-4 text-gray-500 text-xs">
                      <span className="block line-clamp-2 break-words" title={project.created_by?.name ?? undefined}>
                        {project.created_by?.name ?? <span className="text-gray-300">—</span>}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-gray-500 text-xs">
                      <span
                        className="block line-clamp-2 break-words"
                        title={project.members && project.members.length > 0 ? project.members.map(m => m.name).join(', ') : undefined}
                      >
                        {project.members && project.members.length > 0
                          ? project.members.map(m => m.name).join(', ')
                          : <span className="text-gray-300">—</span>}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-gray-700 text-center">
                      {project.files_count ?? <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium max-w-full ${PROJECT_STATUS_TEXT[status]}`}>
                        <span className={`h-2 w-2 rounded-full shrink-0 ${PROJECT_STATUS_DOT[status]}`} />
                        <span className="break-words">{tProjectStatus(status)}</span>
                      </span>
                    </td>
                    <td className="px-5 py-4 text-gray-500 text-xs">
                      {new Date(project.created_at).toLocaleDateString(uiLanguage)}
                    </td>
                    <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-3">
                        {status === ProjectStatus.READY_FOR_REPORT && onRun && (
                          <button
                            onClick={() => onRun(project.project_id)}
                            title={t('runReportTitle')}
                            className="text-gray-400 hover:text-gray-700 transition-colors"
                          >
                            <Play className="h-4 w-4" />
                          </button>
                        )}
                        {onManageCollaborators && isProjectAdmin(project) && (
                          <button
                            onClick={() => onManageCollaborators(project.project_id)}
                            title={t('manageCollaboratorsTitle')}
                            className="text-gray-400 hover:text-gray-700 transition-colors"
                          >
                            <Users className="h-4 w-4" />
                          </button>
                        )}
                        {onDelete && isProjectAdmin(project) && (
                          <button
                            onClick={() => onDelete?.(project.project_id)}
                            title={t('removeProjectTitle')}
                            className="text-gray-400 hover:text-red-600 transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );
}
